import { MessageSquareText, Users, GraduationCap, FileText, Logs, BarChart, LucideProps } from 'lucide-react'
import { HoverEffect } from '@/components/ui/card-hover-effect'
import { useAtomValue } from 'jotai'
import { userAtom } from '@/store/store'
import { UserRoleIndex } from '@/types'

type FeatureData = {
  icon: React.ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>>
  title: string
  description: string
  items: string[]
  href: string
}

const allFeatureData: FeatureData[] = [
  {
    icon: FileText,
    title: 'Manage Documents',
    description: 'Upload, view, and organize course materials efficiently',
    items: [
      'View and search documents uploaded to your courses',
      'Use the built-in viewer for chunk and file views',
      'Add documents to existing courses (up to 10 files per upload)',
    ],
    href: '/documents',
  },
  {
    icon: GraduationCap,
    title: 'Manage Courses',
    description: 'Access and modify course information, system prompts, and assignments.',
    items: [
      'View and search assigned courses in the courses table',
      'View or edit system prompts for each course',
      'Note: Adding courses and assigning multiple instructors are admin-only functions',
    ],
    href: '/courses',
  },
  {
    icon: MessageSquareText,
    title: 'Conversations',
    description: 'Monitor and analyze student interactions with the Chat APP',
    items: [
      'View conversations from the Chat APP for assigned courses',
      'Inspect answer accuracy and identify common student questions',
    ],
    href: '/conversations',
  },
  {
    icon: Users,
    title: 'Manage Users',
    description: 'Admin-only page for user management and access control',
    items: ['Create new users and manage existing accounts', 'Set and modify user roles and access levels'],
    href: '/users',
  },
  {
    icon: Logs,
    title: 'Logs',
    description: 'Access detailed conversation logs for technical insights',
    items: [
      'Search and view log files of Chat APP conversations',
      'Gain insights into AI model usage and data sources',
    ],
    href: '/logs',
  },
  {
    icon: BarChart,
    title: 'Usage Statistics',
    description: 'Admin/Dev-only page to view detailed analytics and usage statistics',
    items: [
      'Monitor course activity and user engagement',
      'Track conversation trends and message patterns',
      'Analyze user participation and activity levels',
      'View course-specific usage statistics',
    ],
    href: '/stats',
  },
]

export default function FeatureDescription() {
  const user = useAtomValue(userAtom)

  const filteredFeatureData = allFeatureData.filter((feature) => {
    if (feature.title === 'Manage Users') {
      return user.userRoleIndex! <= UserRoleIndex.Admin
    }
    if (feature.title === 'Logs' || feature.title === 'Usage Statistics') {
      return user.userRoleIndex === UserRoleIndex.Dev
    }
    return true // Keep other features visible to all roles
  })

  return <HoverEffect data={filteredFeatureData} />
}
